import { NavLink, useLocation } from 'react-router';
import { Lock } from 'lucide-react';
import { featureManifest } from '@app/featureManifest';
import { useTier } from '@features/billing/useTier';
import { ProfileMenu } from './ProfileMenu';
import logoUrl from '../../../assets/logo.svg';
import { BRAND_GRADIENT } from '@shared/styles/brand';

/**
 * Collapsed 48px icon rail (Figma "Sidebar"). Logo on top, the feature-manifest nav as centred
 * icons with hover tooltips, and the profile avatar (with its email / theme / Settings / Log-out
 * dropdown) pinned to the bottom. Active item = a left-edge BRAND-GRADIENT bar + a gradient-stroked
 * icon (2026-07-09 Figma; was a flat --accent bar + --accent-soft chip). `relative z-20` lifts the
 * rail — and its overflowing tooltips/dropdown — above the main content to its right.
 */
export function Sidebar() {
  const items = featureManifest.filter(f => !f.hideFromNav);
  const { pathname } = useLocation();
  const { isPro } = useTier();
  return (
    <aside
      className="relative z-20 hidden w-12 shrink-0 flex-col items-center bg-canvas-raised py-3 md:flex"
      style={{ borderRight: '1px solid var(--canvas-divider)' }}
    >
      {/* Lucide icons stroke with `currentColor`, which can't be a gradient — this zero-size def
          lets the active icon take `stroke: url(#nav-brand-grad)` (CSS stroke beats the svg's
          stroke attribute; the paths inherit it). Direction ≈ the 246.81° brand gradient. */}
      <svg width="0" height="0" className="absolute" aria-hidden focusable="false">
        <defs>
          {/* userSpaceOnUse is REQUIRED, not cosmetic (2026-07-27 bug): with the default
              objectBoundingBox units, SVG does not paint an element whose bbox has zero width or
              height — so every perfectly horizontal/vertical path vanished when its icon went
              active. ListChecks lost its three `h8` rules and kept only the two checkmarks;
              CalendarDays lost its `M3 10h18`. Coordinates span the 24×24 lucide viewBox along the
              brand diagonal (the 19×19 sparkle simply uses a slightly narrower slice of the ramp). */}
          <linearGradient id="nav-brand-grad" gradientUnits="userSpaceOnUse" x1="24" y1="0" x2="0" y2="24">
            <stop offset="13.39%" stopColor="#009AC8" />
            <stop offset="86.63%" stopColor="#1BB980" />
          </linearGradient>
        </defs>
      </svg>
      {/* Logo — the brand knot (same asset as the login pages; 2026-07-08: replaced the generic
          Activity placeholder to match Figma). Gradient mark on transparent — works in both themes. */}
      <div className="flex h-8 w-8 items-center justify-center">
        <img src={logoUrl} alt="" aria-hidden className="h-8 w-8" />
      </div>

      {/* Nav — icon-only, tooltip on hover. */}
      <nav className="mt-4 flex w-full flex-1 flex-col items-center gap-1">
        {items.map(({ id, path, navLabel, navIcon: Icon, navAliases, requiresPro }) => {
          // Active also for aliased routes (e.g. /transactions keeps "Trades" lit) — NavLink's own
          // isActive only matches `to`, so we compute it here and drive the visuals from it.
          const active = pathname === path || (navAliases?.includes(pathname) ?? false);
          const locked = !!requiresPro && !isPro;
          // I6: the lock is folded into the NavLink's OWN accessible name ("Backtesting AI (requires
          // Pro)") instead of a second, separately-announced inner label — a screen reader landing on
          // the link hears one coherent name rather than the link's label followed by an unrelated
          // "Requires Pro" image announcement.
          return (
            <NavLink
              key={id}
              to={path}
              aria-label={locked ? `${navLabel} (requires Pro)` : navLabel}
              className="group relative flex h-10 w-full items-center justify-center"
            >
              {/* Left gradient indicator (active only, Figma). */}
              {active && (
                <span
                  className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full"
                  style={{ background: BRAND_GRADIENT }}
                />
              )}
              {/* Icon — gradient-stroked when active (via the #nav-brand-grad def above). Stays a
                  normal, clickable nav item even when locked (Pro-gated): it lands on the feature's
                  own in-page ProUpsell rather than being a dead click, so the lock is a hint, not a
                  block. */}
              <span
                className={`relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                  active ? '[&_svg]:[stroke:url(#nav-brand-grad)]' : 'text-text-muted group-hover:bg-canvas-inset group-hover:text-text'
                }`}
              >
                <Icon className="h-5 w-5" />
                {/* Purely decorative now that the lock is folded into the NavLink's aria-label above —
                    no separate role/label, or a screen reader would hear it twice. */}
                {locked && (
                  <span
                    aria-hidden
                    className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full"
                    style={{ background: 'var(--canvas-raised)', boxShadow: '0 0 0 1px var(--canvas-divider)' }}
                  >
                    <Lock className="h-2 w-2 text-text-muted" aria-hidden />
                  </span>
                )}
              </span>
              {/* Tooltip. */}
              <span
                className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-lg px-2 py-1 text-xs opacity-0 shadow-card transition-opacity group-hover:opacity-100"
                style={{ background: 'var(--canvas-raised)', color: 'var(--text)', border: '1px solid var(--canvas-divider)' }}
              >
                {navLabel}
              </span>
            </NavLink>
          );
        })}
      </nav>

      {/* Profile — avatar + dropdown (email / theme / Settings / Log out). */}
      <ProfileMenu />
    </aside>
  );
}
